import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Sun, Moon, LogIn, LayoutDashboard, CreditCard, Radar, Menu, X, Building2 } from 'lucide-react';

const DARK_LOGO = "https://customer-assets.emergentagent.com/job_vaptara-vuln-ai/artifacts/znyb36lz_vaptara_logo_w.png";
const LIGHT_LOGO = "https://customer-assets.emergentagent.com/job_vaptara-vuln-ai/artifacts/z7et0mch_Vaptara%20logo%20cropped.png";

const NAV = [
  { to: '/free-scan', label: 'Free Scanners', icon: Radar, testid: 'public-nav-free-scan' },
  { to: '/plans', label: 'Pricing', icon: CreditCard, testid: 'public-nav-pricing' },
  { to: '/login?next=/org-security', label: 'For Organizations', icon: Building2, testid: 'public-nav-org' },
];

export const PublicHeader = ({ usage }) => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const go = (to) => { setOpen(false); navigate(to); };
  return (
    <header className="sticky top-0 z-40 border-b border-border/60 backdrop-blur-xl bg-background/70" data-testid="public-header">
      <div className="max-w-7xl mx-auto h-16 flex items-center px-4 md:px-8 gap-3">
        <Link to="/free-scan" className="flex items-center gap-2 shrink-0" data-testid="public-logo">
          <img src={theme === 'dark' ? DARK_LOGO : LIGHT_LOGO} alt="Vaptara" className="h-8" />
        </Link>
        <nav className="ml-6 hidden md:flex items-center gap-1">
          {NAV.map(n => <Button key={n.to} variant="ghost" size="sm" className="gap-1.5 rounded-full" onClick={() => navigate(n.to)} data-testid={n.testid}><n.icon className="h-4 w-4" /> {n.label}</Button>)}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          {usage && (
            <button onClick={() => document.getElementById('scanner')?.scrollIntoView({ behavior: 'smooth' })} className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors hover:bg-muted ${usage.exhausted ? 'border-severity-high/40 text-severity-high bg-severity-high/10' : 'border-primary/30 text-primary bg-primary/5'}`} data-testid="header-quota-pill">
              <span className={`h-1.5 w-1.5 rounded-full ${usage.exhausted ? 'bg-severity-high' : 'bg-primary animate-pulse'}`} />
              {usage.remaining} / {usage.limit} free scans available
            </button>
          )}
          <Button variant="ghost" size="icon" className="rounded-full" onClick={toggleTheme} data-testid="public-theme-toggle">
            {theme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </Button>
          {user ? (
            <Button size="sm" className="gap-1.5 rounded-full btn-glow" onClick={() => navigate('/dashboard')} data-testid="public-dashboard-btn"><LayoutDashboard className="h-4 w-4" /> Dashboard</Button>
          ) : (
            <Button size="sm" className="gap-1.5 rounded-full btn-glow" onClick={() => navigate('/login')} data-testid="public-signin-btn"><LogIn className="h-4 w-4" /> Sign In</Button>
          )}
          <Button variant="ghost" size="icon" className="md:hidden rounded-full" onClick={() => setOpen(o => !o)} data-testid="public-menu-toggle">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</Button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t bg-background/95 backdrop-blur-xl px-4 py-3 space-y-1" data-testid="public-mobile-menu">
          {NAV.map(n => <button key={n.to} onClick={() => go(n.to)} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-muted text-sm font-medium"><n.icon className="h-4 w-4 text-primary" /> {n.label}</button>)}
          {usage && <p className="px-3 pt-2 text-xs text-muted-foreground">{usage.remaining} / {usage.limit} free scans available</p>}
        </div>
      )}
    </header>
  );
};
